import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, MapPin, Heart, Compass, Sparkles, Plus, BarChart3, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import EventMediaGallery from '../components/EventMediaGallery';
import { PAYMENTS_ENABLED } from '../config/features';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import FavoriteButton from '../components/FavoriteButton';
import CreateEventForm from '../components/CreateEventForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const { user, becomeOrganizer } = useAuth();
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [solidarity, setSolidarity] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [nearby, setNearby] = useState([]);
  const [forYou, setForYou] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locationStatus, setLocationStatus] = useState('idle'); // idle, loading, granted, denied
  const [showBecomeOrgDialog, setShowBecomeOrgDialog] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    loadEvents();
    // Auto-request geolocation on mount. Silent — no popup nagging if denied.
    // Users can still tap "Activar ubicación" later.
    if (navigator.geolocation && !localStorage.getItem('mitribu_geo_denied')) {
      requestLocation({ silent: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadEvents = async () => {
    try {
      const [featuredRes, solidarityRes] = await Promise.all([
        axios.get(`${API}/events/featured?limit=6`),
        axios.get(`${API}/events?is_solidarity=true&limit=6`)
      ]);
      setFeatured(featuredRes.data);
      setSolidarity(solidarityRes.data);

      // Load "Para Vos" based on user interests
      if (user && user.interests && user.interests.length > 0) {
        const interestPromises = user.interests.slice(0, 3).map(interest =>
          axios.get(`${API}/events?category=${interest}&limit=3`)
        );
        const results = await Promise.all(interestPromises);
        const allForYou = results.flatMap(r => r.data);
        // Deduplicate
        const unique = Array.from(new Map(allForYou.map(e => [e.event_id, e])).values());
        setForYou(unique.slice(0, 6));
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const requestLocation = ({ silent = false } = {}) => {
    if (!navigator.geolocation) {
      setLocationStatus('denied');
      return;
    }

    setLocationStatus('loading');
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const response = await axios.get(
            `${API}/events/nearby?lat=${position.coords.latitude}&lng=${position.coords.longitude}&radius_km=20&limit=6`
          );
          setNearby(response.data);
          setLocationStatus('granted');
          localStorage.removeItem('mitribu_geo_denied');
        } catch (error) {
          setLocationStatus('denied');
        }
      },
      () => {
        setLocationStatus(silent ? 'idle' : 'denied');
        if (!silent) localStorage.setItem('mitribu_geo_denied', '1');
      },
      { timeout: 10000 }
    );
  };

  const handlePublishClick = () => {
    if (!user) {
      navigate('/register?intent=publish');
      return;
    }
    if (user.role === 'organizer' || user.role === 'admin') {
      navigate('/organizador');
      return;
    }
    setShowBecomeOrgDialog(true);
  };

  const handleConfirmBecomeOrganizer = async () => {
    try {
      setUpgrading(true);
      await becomeOrganizer();
      setShowBecomeOrgDialog(false);
      toast.success('¡Ahora sos organizador! Creá tu primer evento.');
      navigate('/organizador');
    } catch (err) {
      toast.error('No pudimos actualizar tu cuenta. Intentá de nuevo.');
    } finally {
      setUpgrading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 border-b-2 border-[#0A0A0A]" style={{background: 'linear-gradient(135deg, #FAFAFA 0%, #FFFFFF 50%, #FFF7ED 100%)'}}>
        <div className="max-w-7xl mx-auto text-center" data-testid="hero-section">
          <span className="inline-block bg-[#0A0A0A] text-white text-xs font-bold tracking-widest px-3 py-1 rounded mb-6">
            SALTA · NOA · 2026
          </span>
          {user && (
            <p className="text-sm text-[#0A0A0A] font-semibold mb-3" data-testid="hero-welcome">
              ¡Hola, {user.name}! 👋
            </p>
          )}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black text-[#0A0A0A] mb-6 tracking-tight">
            Descubrí eventos
            <span className="block text-primary mt-2">en Salta y el NOA</span>
          </h1>
          <p className="text-lg sm:text-xl text-[#525252] max-w-3xl mx-auto mb-8 leading-relaxed">
            Conectá con tu comunidad. Eventos culturales, sociales, deportivos y solidarios cerca tuyo.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/explorar">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-white font-semibold px-8" data-testid="explore-button">
                Explorar eventos
              </Button>
            </Link>
            {user ? (
              <Button
                size="lg"
                onClick={() => setShowCreateForm(true)}
                className="bg-[#0A0A0A] hover:bg-[#171717] text-white font-semibold"
                data-testid="home-new-event-button"
              >
                <Plus className="w-5 h-5 mr-2" />
                Crear evento nuevo
              </Button>
            ) : (
              <Button
                size="lg"
                variant="outline"
                onClick={handlePublishClick}
                className="border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white font-semibold"
                data-testid="publish-cta-button"
              >
                <Plus className="w-5 h-5 mr-2" />
                Publicar evento
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Prominent Cerca de Mí section — dark themed, right below the hero */}
      <section className="bg-[#0A0A0A] text-white py-14 px-4 sm:px-6 lg:px-8" data-testid="cerca-de-mi-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                <Compass className="text-white w-7 h-7" />
              </div>
              <div>
                <h2 className="text-3xl sm:text-4xl font-heading font-black tracking-tight text-white">Cerca de mí</h2>
                <p className="text-white/70 mt-1 text-sm">
                  {locationStatus === 'granted'
                    ? `${nearby.length} evento${nearby.length === 1 ? '' : 's'} a menos de 20 km`
                    : 'Descubrí lo que pasa cerca tuyo, ahora mismo'}
                </p>
              </div>
            </div>
            {locationStatus !== 'granted' && (
              <Button
                onClick={() => requestLocation()}
                disabled={locationStatus === 'loading'}
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white font-semibold shadow-lg"
                data-testid="enable-location-button"
              >
                <Compass className="w-4 h-4 mr-2" />
                {locationStatus === 'loading' ? 'Detectando...' : 'Activar ubicación'}
              </Button>
            )}
          </div>

          {locationStatus === 'granted' && nearby.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="nearby-grid">
              {nearby.map(event => <EventCard key={event.event_id} event={event} />)}
            </div>
          )}

          {locationStatus === 'granted' && nearby.length === 0 && (
            <div className="border border-white/20 rounded-lg p-8 text-center">
              <p className="text-white/80 mb-3">No hay eventos en un radio de 20 km todavía.</p>
              <Link to="/explorar"><Button className="bg-primary hover:bg-primary/90 text-white">Explorar todo el NOA</Button></Link>
            </div>
          )}

          {locationStatus === 'denied' && (
            <div className="border border-white/20 rounded-lg p-8 text-center" data-testid="location-denied">
              <p className="text-white/80 mb-2">No pudimos acceder a tu ubicación 📍</p>
              <p className="text-sm text-white/60 mb-4">Habilitala desde el candado del navegador y volvé a intentar.</p>
              <Button onClick={() => requestLocation()} className="bg-primary hover:bg-primary/90 text-white">Reintentar</Button>
            </div>
          )}

          {locationStatus === 'idle' && (
            <div className="border border-dashed border-white/30 rounded-lg p-10 text-center" data-testid="location-idle">
              <Compass className="w-12 h-12 text-white/40 mx-auto mb-3" />
              <p className="text-white/80 mb-2">Activá tu ubicación para ver eventos en tu zona.</p>
              <p className="text-sm text-white/50">Nunca guardamos tu posición.</p>
            </div>
          )}

          {locationStatus === 'loading' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="location-loading">
              {[1,2,3].map(i => (
                <div key={i} className="animate-pulse bg-white/5 rounded-lg h-64"></div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Organizer CTA Banner - only visible for organizers and admins */}
      {user && (user.role === 'organizer' || user.role === 'admin') && (
        <section className="py-8 px-4 sm:px-6 lg:px-8 bg-[#FFF7ED] border-b border-[#FED7AA]" data-testid="organizer-cta-banner">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                  <Plus className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-heading font-bold text-[#0A0A0A]">¡Hola, organizador!</h3>
                  <p className="text-sm text-[#525252]">Creá tu próximo evento o revisá estadísticas de los actuales</p>
                </div>
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <Link to="/organizador" className="flex-1 sm:flex-none">
                  <Button className="w-full bg-primary hover:bg-primary/90 text-white" data-testid="home-create-event-cta">
                    <Plus className="w-4 h-4 mr-2" />
                    Crear evento
                  </Button>
                </Link>
                <Link to="/organizador" className="flex-1 sm:flex-none">
                  <Button variant="outline" className="w-full border-[#E5E5E5] text-[#0A0A0A]" data-testid="home-analytics-cta">
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Analytics
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Featured Events */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" data-testid="featured-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-[#0A0A0A] tracking-tight">Destacados</h2>
              <p className="text-[#737373] mt-2">Los eventos más populares de la semana</p>
            </div>
            <Link to="/explorar">
              <Button variant="ghost" className="text-primary hover:text-primary/80">Ver todos</Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => <div key={i} className="animate-pulse"><div className="bg-white rounded-lg h-64"></div></div>)}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featured.map(event => <EventCard key={event.event_id} event={event} />)}
            </div>
          )}
        </div>
      </section>

      {/* Para Vos - based on interests */}
      {user && forYou.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white/50" data-testid="for-you-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Sparkles className="text-primary w-8 h-8" />
              <div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-[#0A0A0A] tracking-tight">
                  Para Vos
                </h2>
                <p className="text-[#737373] mt-1">Basado en tus intereses: {user.interests.join(', ')}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {forYou.map(event => <EventCard key={event.event_id} event={event} />)}
            </div>
          </div>
        </section>
      )}

      {/* Solidarity Events */}
      {solidarity.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white/50" data-testid="solidarity-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <Heart className="text-primary w-8 h-8" />
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-[#0A0A0A] tracking-tight">
                    Ayudá a tu Tribu
                  </h2>
                </div>
                <p className="text-[#737373]">Eventos solidarios que necesitan tu apoyo</p>
              </div>
              <Link to="/explorar?solidarity=true">
                <Button variant="ghost" className="text-primary hover:text-primary/80">Ver todos</Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {solidarity.map(event => <EventCard key={event.event_id} event={event} solidarity />)}
            </div>
          </div>
        </section>
      )}

      {/* Categories */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-[#0A0A0A] mb-8 text-center tracking-tight">
            Explorá por categoría
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {['Arte', 'Cultura', 'Deporte', 'Educación', 'Gastronomía', 'Música', 'Tecnología', 'Turismo'].map(cat => (
              <Link key={cat} to={`/explorar?category=${cat}`}>
                <div className="bg-white border border-[#E5E5E5] rounded-lg p-6 text-center hover:border-primary transition-colors cursor-pointer" data-testid={`category-${cat.toLowerCase()}`}>
                  <p className="text-[#0A0A0A] font-semibold">{cat}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      {/* Become Organizer confirmation */}
      <AlertDialog open={showBecomeOrgDialog} onOpenChange={setShowBecomeOrgDialog}>
        <AlertDialogContent data-testid="become-organizer-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Convertite en organizador</AlertDialogTitle>
            <AlertDialogDescription>
              Para publicar eventos en Mi Tribu necesitás una cuenta de organizador.
              Es gratis y lo activamos al instante. Vas a poder crear eventos, ver
              analytics y vender entradas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="become-organizer-cancel">Ahora no</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmBecomeOrganizer}
              disabled={upgrading}
              data-testid="become-organizer-confirm"
            >
              {upgrading ? 'Activando...' : 'Ser organizador'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {showCreateForm && (
        <CreateEventForm
          onClose={() => setShowCreateForm(false)}
          onSuccess={() => {
            setShowCreateForm(false);
            toast.success('¡Tu evento fue creado! Un administrador lo revisará antes de publicarlo.');
          }}
        />
      )}
    </div>
  );
};

const EventCard = ({ event, solidarity }) => {
  return (
    <Link to={`/eventos/${event.event_id}`} data-testid={`event-card-${event.event_id}`}>
      <div className="group bg-white border border-[#E5E5E5] rounded-lg overflow-hidden hover:border-primary transition-all hover:-translate-y-1">
        <div className="relative aspect-[16/10] bg-[#F5F5F5]">
          <FavoriteButton eventId={event.event_id} variant="overlay" />
          <EventMediaGallery
            images={event.images || []}
            videoUrl={event.video_url || null}
            alt={event.title}
            variant="card"
          />
          {solidarity && (
            <div className="absolute top-3 left-3 bg-primary text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
              <Heart className="w-4 h-4" />
              Solidario
            </div>
          )}
        </div>

        <div className="p-4 space-y-2">
          <div className="flex items-center gap-2 text-xs text-[#737373]">
            <span className="px-2 py-1 bg-primary/10 text-primary rounded">{event.category}</span>
            {event.price === 0 ? (
              <span className="px-2 py-1 bg-green-500/10 text-green-400 rounded">Gratis</span>
            ) : PAYMENTS_ENABLED ? (
              <span className="px-2 py-1 bg-[#F5F5F5] text-[#525252] rounded">{event.currency} {event.price}</span>
            ) : null}
          </div>
          <h3 className="text-lg font-semibold text-[#0A0A0A] group-hover:text-primary transition-colors line-clamp-2">
            {event.title}
          </h3>
          {event.rating_count > 0 && (
            <div className="flex items-center gap-1 text-sm">
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <span className="font-semibold text-[#0A0A0A]">{event.rating_avg?.toFixed(1)}</span>
              <span className="text-[#737373]">· {event.rating_count}</span>
            </div>
          )}
          <p className="text-sm text-[#737373] line-clamp-2">{event.description_short}</p>
          <div className="flex items-center gap-4 text-sm text-[#737373] pt-2 border-t border-[#E5E5E5]">
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <span>{format(new Date(event.start_date), 'd MMM', { locale: es })}</span>
            </div>
            {event.location && (
              <div className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                <span>{event.location.city}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default Home;
